<script setup>
import { computed } from 'vue';
import { useStoreGetters } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { frontendURL } from 'dashboard/helper/URLHelper';
import { useBranding } from 'shared/composables/useBranding';

import { Badge } from 'dashboard/components-next/polaris/ui/badge';
import { Button } from 'dashboard/components-next/polaris/ui/button';
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemMedia,
  ItemTitle,
} from 'dashboard/components-next/polaris/ui/item';

// Phase 9.9 (the kit's ChannelDirectory card): the integration's logo (the woot's image, light and dark), its
// name and the enabled/disabled state the woot computes, one sentence, and "Configure".

const props = defineProps({
  id: {
    type: [String, Number],
    required: true,
  },
  name: {
    type: String,
    default: '',
  },
  description: {
    type: String,
    default: '',
  },
  enabled: {
    type: Boolean,
    default: false,
  },
});

const getters = useStoreGetters();
const accountId = getters.getCurrentAccountId;

const { t } = useI18n();
const { replaceInstallationName } = useBranding();

const integrationStatus = computed(() =>
  props.enabled
    ? t('INTEGRATION_APPS.STATUS.ENABLED')
    : t('INTEGRATION_APPS.STATUS.DISABLED')
);

const actionURL = computed(() =>
  frontendURL(`accounts/${accountId.value}/settings/integrations/${props.id}`)
);
</script>

<template>
  <Item
    variant="outline"
    size="sm"
    class="h-full flex-nowrap items-start"
    data-integration-card
  >
    <ItemMedia variant="image" class="size-10">
      <img
        :src="`/dashboard/images/integrations/${id}.png`"
        alt=""
        class="block dark:hidden"
      />
      <img
        :src="`/dashboard/images/integrations/${id}-dark.png`"
        alt=""
        class="hidden dark:block"
      />
    </ItemMedia>
    <ItemContent class="min-w-0 gap-1">
      <ItemTitle class="flex-wrap">
        {{ name }}
        <Badge :variant="enabled ? 'success' : 'outline'">
          {{ integrationStatus }}
        </Badge>
      </ItemTitle>
      <ItemDescription class="line-clamp-none text-wrap type-polaris-caption">
        {{ replaceInstallationName(description) }}
      </ItemDescription>
    </ItemContent>
    <ItemActions>
      <Button variant="outline" size="sm" as-child>
        <router-link :to="actionURL">
          {{ $t('INTEGRATION_APPS.CONFIGURE') }}
        </router-link>
      </Button>
    </ItemActions>
  </Item>
</template>
